import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Loader2, RefreshCw, Save, Search, Send, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const STATUS_STYLES = {
  new: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  unchanged: 'bg-gray-100 text-gray-700',
  error: 'bg-red-100 text-red-800',
  row_error: 'bg-red-100 text-red-800',
};
const STATUS_LABELS = { new: 'New', update: 'Update', unchanged: 'Nothing to update', error: 'Method error', row_error: 'Row error' };
const FILTERS = [
  { value: '', label: 'All rows', count: (s) => s.rows },
  { value: 'new', label: 'New', count: (s) => s.new },
  { value: 'update', label: 'Update', count: (s) => s.update },
  { value: 'unchanged', label: 'Nothing to update', count: (s) => s.unchanged },
  { value: 'error', label: 'Method errors', count: (s) => s.error },
  { value: 'row_error', label: 'Row errors', count: (s) => s.row_errors },
];

const api = (uploadId, path = '') => `${API_URL}/lca-impact-factors/uploads/${uploadId}${path}`;
const errorMessage = (error) => error.response?.data?.detail || error.message;
const frameworkLabel = (framework) => framework || 'ISO';
const formatFactor = (value) => (typeof value === 'number' ? Number(value.toPrecision(6)).toString() : String(value ?? '—'));
const taxonomy = (fields) => [fields.category, fields.sub_category, fields.type, fields.sub_type].filter(Boolean).join(' / ') || '—';
const location = (fields) => [fields.city, fields.state, fields.country, fields.continent].filter((v) => v && v !== '*').join(', ') || 'Global';

// A row can go to the approvers when at least one of its methods adds or updates something
const isSubmittable = (review) => !review.errors.length && Boolean(review.counts.new || review.counts.update);

function StatusChip({ status, children }) {
  return <span className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${STATUS_STYLES[status]}`}>{children ?? STATUS_LABELS[status]}</span>;
}

function MethodChips({ review }) {
  const { counts, update_ids: updates } = review;
  return (
    <div className="flex flex-wrap gap-1">
      {review.errors.length > 0 && <StatusChip status="row_error">{review.errors.length} row error(s)</StatusChip>}
      {['new', 'update', 'unchanged', 'error'].filter((s) => counts[s]).map((s) => (
        <StatusChip key={s} status={s}>{counts[s]} {STATUS_LABELS[s].toLowerCase()}</StatusChip>
      ))}
      {updates.length > 0 && updates.length <= 3 && <span className="text-xs text-muted-foreground">IFID {updates.join(', ')}</span>}
    </div>
  );
}

function EntryTable({ title, rows }) {
  if (!rows.length) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-1">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border rounded">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-1 text-left">Category</th>
              <th className="p-1 text-left">Indicator</th>
              <th className="p-1 text-left">Framework</th>
              <th className="p-1 text-left">Unit</th>
              <th className="p-1 text-right">Factor</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ entry, old }, idx) => (
              <tr key={idx} className="border-t">
                <td className="p-1">{entry.impactCategory}</td>
                <td className="p-1">{entry.indicator || '—'}</td>
                <td className="p-1 whitespace-nowrap">{frameworkLabel(entry.framework)}</td>
                <td className="p-1 whitespace-nowrap">{entry.unit}</td>
                <td className="p-1 text-right font-mono whitespace-nowrap">
                  {old !== undefined && <span className="text-red-600 line-through mr-2">{formatFactor(old)}</span>}
                  <span className={old !== undefined ? 'text-green-700' : ''}>{formatFactor(entry.factor)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MethodSection({ plan, values, headers, onValueChange }) {
  const [open, setOpen] = useState(plan.status === 'update' || plan.status === 'error');
  const label = plan.status === 'update'
    ? `${plan.duplicate_of ? 'Duplicate — updates' : 'Updates'} IFID ${plan.impact_factor_id}`
    : undefined;
  return (
    <div className="border rounded bg-white" data-testid={`review-method-${plan.method}`}>
      <button type="button" className="flex items-center gap-2 w-full text-left text-sm p-2" onClick={() => setOpen((v) => !v)}>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <span className="font-medium">{plan.method}</span>
        <StatusChip status={plan.status}>{label}</StatusChip>
        {plan.warnings.length > 0 && <AlertTriangle className="w-4 h-4 text-amber-600" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          {plan.error && <div className="text-xs text-red-700">{plan.error}</div>}
          {plan.warnings.map((w) => <div key={w} className="text-xs text-amber-700">{w}</div>)}
          {plan.field_changes.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">Fields that will change</div>
              <ul className="text-xs space-y-0.5">
                {plan.field_changes.map((c) => (
                  <li key={c.field}>
                    {headers[c.field] || c.field}: <span className="text-red-600 line-through">{String(c.old ?? '—')}</span> → <span className="text-green-700">{String(c.new ?? '—')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <EntryTable title="Impact factors that will change" rows={plan.changed.map((c) => ({ entry: c.new, old: c.old.factor }))} />
          <EntryTable title={plan.status === 'new' ? 'Impact factors' : 'Impact factors that will be added'} rows={plan.added.map((entry) => ({ entry }))} />
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Values in the file</div>
            <div className="space-y-1">
              {values.map(({ value, index }) => (
                <label key={index} className="text-xs flex items-center gap-2">
                  <span className="flex-1 truncate" title={`${value.category} — ${value.indicator} (${value.unit})`}>
                    {value.category} <span className="text-muted-foreground">— {value.indicator} ({value.unit})</span>
                  </span>
                  <Input className="h-7 w-36 text-xs font-mono" value={value.value ?? ''} onChange={(e) => onValueChange(index, e.target.value)} />
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RowPanel({ uploadId, rowNumber, columns, editable, onClose, onSaved }) {
  const [row, setRow] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c.header])), [columns]);

  useEffect(() => {
    setRow(null);
    axios.get(api(uploadId, `/rows/${rowNumber}`))
      .then((res) => { setRow(res.data); setDraft({ fields: res.data.fields, values: res.data.values }); })
      .catch((error) => toast.error(`Failed to load row ${rowNumber}: ${errorMessage(error)}`));
  }, [uploadId, rowNumber]);

  const valuesByMethod = useMemo(() => (draft?.values || []).reduce((acc, value, index) => {
    (acc[value.method] = acc[value.method] || []).push({ value, index });
    return acc;
  }, {}), [draft]);
  const dirty = row && draft && JSON.stringify({ fields: row.fields, values: row.values }) !== JSON.stringify(draft);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(api(uploadId, `/rows/${rowNumber}`), draft);
      setRow({ ...row, ...res.data });
      setDraft({ fields: res.data.fields, values: res.data.values });
      onSaved();
      toast.success(`Row ${rowNumber} saved`);
    } catch (error) {
      toast.error(`Save failed: ${errorMessage(error)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-gray-200" data-testid={`review-details-${rowNumber}`}>
      <CardContent className="p-0">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
          <div className="min-w-0">
            <div className="font-semibold">Row {rowNumber}</div>
            {row && <div className="text-xs text-muted-foreground truncate">{taxonomy(row.fields)} · {location(row.fields)}</div>}
          </div>
          <div className="flex items-center gap-2">
            {editable && (
              <Button size="sm" className="btn-primary" onClick={save} disabled={!dirty || saving} data-testid="review-row-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save row
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>
        {!row ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="p-4 space-y-4 max-h-[calc(100vh-220px)] overflow-y-auto">
            {dirty && <div className="text-xs text-amber-700">Unsaved changes — save the row to see what they will do.</div>}
            {row.errors.length > 0 && <ul className="text-xs text-red-700 list-disc pl-5">{row.errors.map((e) => <li key={e}>{e}</li>)}</ul>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {columns.map((column) => (
                <label key={column.key} className="text-xs">
                  <span className="text-muted-foreground">{column.header}</span>
                  <Input className="h-8 text-xs mt-0.5" value={draft.fields[column.key] ?? ''} disabled={!editable}
                    onChange={(e) => setDraft((d) => ({ ...d, fields: { ...d.fields, [column.key]: e.target.value } }))}
                    data-testid={`review-field-${column.key}`} />
                </label>
              ))}
            </div>
            <div className="space-y-2">
              {row.methods.map((plan) => (
                <MethodSection key={plan.method} plan={plan} values={valuesByMethod[plan.method] || []} headers={headers}
                  onValueChange={(index, value) => setDraft((d) => ({ ...d, values: d.values.map((v, i) => (i === index ? { ...v, value } : v)) }))} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// The uploader's check of an uploaded sheet before it goes to the approvers. The draft lives on the server, so a
// sheet of thousands of rows is paged, filtered and searched there; a row's full plan loads when it is opened.
export default function LcaUploadReviewPage() {
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const [upload, setUpload] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [reload, setReload] = useState(0);

  const loadUpload = useCallback(() => axios.get(api(uploadId))
    .then((res) => setUpload(res.data))
    .catch((error) => toast.error(`Failed to load the upload: ${errorMessage(error)}`)), [uploadId]);

  useEffect(() => { loadUpload(); }, [loadUpload]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { setPage(1); }, [status, debouncedSearch, pageSize]);

  useEffect(() => {
    setLoading(true);
    const params = { page, page_size: pageSize };
    if (status) params.status = status;
    if (debouncedSearch) params.search = debouncedSearch;
    axios.get(api(uploadId, '/rows'), { params })
      .then((res) => { setRows(res.data.items); setTotal(res.data.total); setTotalPages(res.data.total_pages); })
      .catch((error) => toast.error(`Failed to load rows: ${errorMessage(error)}`))
      .finally(() => setLoading(false));
  }, [uploadId, page, pageSize, status, debouncedSearch, reload]);

  const editable = upload?.status === 'draft';
  const refresh = () => { setReload((n) => n + 1); loadUpload(); };

  const setExcluded = async (rowNumbers, excluded) => {
    try {
      await axios.put(api(uploadId, '/exclusions'), { rows: rowNumbers, excluded });
      setRows((current) => current.map((r) => (rowNumbers.includes(r.row) ? { ...r, excluded } : r)));
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const recheck = async () => {
    setBusy('recheck');
    try {
      const res = await axios.post(api(uploadId, '/recheck'));
      setUpload(res.data);
      setReload((n) => n + 1);
      toast.success('Every row checked again');
    } catch (error) {
      toast.error(`Re-check failed: ${errorMessage(error)}`);
    } finally {
      setBusy(null);
    }
  };

  const submit = async () => {
    setBusy('submit');
    try {
      const res = await axios.post(api(uploadId, '/submit'));
      navigate('/lca-impact-factors', { state: { uploadResults: res.data } });
    } catch (error) {
      toast.error(`Submit failed: ${errorMessage(error)}`);
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Discard this upload? Nothing from it will be sent to the approvers.')) return;
    try {
      await axios.delete(api(uploadId));
      navigate('/lca-impact-factors');
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const pageSubmittable = rows.filter((r) => isSubmittable(r.review)).map((r) => r.row);
  const pageAllIncluded = pageSubmittable.length > 0 && rows.every((r) => !isSubmittable(r.review) || !r.excluded);
  const summary = upload?.summary;

  return (
    <DashboardLayout>
      <div className="space-y-4 animate-fade-in" data-testid="lca-upload-review-page">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <Button variant="ghost" size="sm" className="-ml-2 mb-1" onClick={() => navigate('/lca-impact-factors')}><ArrowLeft className="w-4 h-4 mr-1" />LCA Impact Factors</Button>
            <h1 className="font-heading text-2xl font-bold text-foreground">Review upload{upload ? `: ${upload.file_name}` : ''}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {editable ? 'Check what every row will do, fix what is wrong, untick rows to leave out, then submit for approval.'
                : `This upload was ${upload?.status || '…'}.`}
            </p>
          </div>
          {editable && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={cancel} disabled={!!busy} data-testid="review-cancel"><Trash2 className="w-4 h-4 mr-2" />Discard</Button>
              <Button variant="outline" onClick={recheck} disabled={!!busy} data-testid="review-recheck">
                {busy === 'recheck' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Re-check all
              </Button>
              <Button className="btn-primary" onClick={submit} disabled={!!busy || upload?.stale} data-testid="review-submit"
                title={upload?.stale ? 'Re-check all first' : undefined}>
                {busy === 'submit' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Submit for approval
              </Button>
            </div>
          )}
        </div>

        {upload?.stale && editable && (
          <div className="text-sm rounded border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2">
            Rows were edited. Re-check all before submitting, so they are also compared with the rest of the file.
          </div>
        )}

        {summary && (
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((filter) => (
              <button key={filter.value} type="button" onClick={() => setStatus(filter.value)} data-testid={`review-filter-${filter.value || 'all'}`}
                className={`px-3 py-1.5 rounded-full border text-sm ${status === filter.value ? 'bg-foreground text-background border-foreground' : 'bg-white hover:bg-gray-50'}`}>
                {filter.label} <span className="opacity-70">{filter.count(summary) ?? 0}</span>
              </button>
            ))}
            {summary.excluded_rows > 0 && <span className="text-sm text-muted-foreground self-center">{summary.excluded_rows} row(s) left out at the last check</span>}
          </div>
        )}

        <div className={`grid gap-4 ${selected ? 'xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]' : 'grid-cols-1'}`}>
          <Card className="border-gray-200 min-w-0">
            <CardContent className="p-0">
              <div className="p-3 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search category, dataset, country, source..." value={search} onChange={(e) => setSearch(e.target.value)}
                    className="pl-10" data-testid="review-search" />
                </div>
              </div>
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
              ) : total === 0 ? (
                <div className="text-center py-12 text-muted-foreground">No rows match</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="min-w-[760px]">
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="table-dense w-10">
                          {editable && (
                            <Checkbox checked={pageAllIncluded} disabled={!pageSubmittable.length} title="Include every row on this page"
                              onCheckedChange={(checked) => setExcluded(pageSubmittable, !checked)} data-testid="review-include-page" />
                          )}
                        </TableHead>
                        <TableHead className="table-dense w-16">Row</TableHead>
                        <TableHead className="table-dense min-w-[240px]">Category</TableHead>
                        <TableHead className="table-dense min-w-[110px]">Location</TableHead>
                        <TableHead className="table-dense min-w-[200px]">Source dataset</TableHead>
                        <TableHead className="table-dense min-w-[200px]">What it does</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.row} onClick={() => setSelected(row.row)} data-testid={`review-row-${row.row}`}
                          className={`table-dense cursor-pointer ${selected === row.row ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            {editable && (
                              <Checkbox checked={isSubmittable(row.review) && !row.excluded} disabled={!isSubmittable(row.review)}
                                onCheckedChange={(checked) => setExcluded([row.row], !checked)} data-testid={`review-include-${row.row}`} />
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{row.row}</TableCell>
                          <TableCell className="text-sm">{taxonomy(row.fields)}</TableCell>
                          <TableCell className="text-sm">{location(row.fields)}</TableCell>
                          <TableCell className="text-sm">{row.fields.source_dataset_name || '—'}</TableCell>
                          <TableCell><MethodChips review={row.review} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {!loading && total > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span>{((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}</span>
                    <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                      <SelectTrigger className="w-[76px] h-8" data-testid="review-page-size"><SelectValue /></SelectTrigger>
                      <SelectContent>{PAGE_SIZE_OPTIONS.map((size) => <SelectItem key={size} value={String(size)}>{size}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft className="w-4 h-4" /></Button>
                    <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {selected && upload && (
            <div className="min-w-0 xl:sticky xl:top-4 self-start">
              <RowPanel uploadId={uploadId} rowNumber={selected} columns={upload.columns} editable={editable}
                onClose={() => setSelected(null)} onSaved={refresh} />
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
